import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrderDetails,
  updateOrderStatus,
  selectOrderDetails,
  clearOrderDetails,
} from "../../../slice/order/orderSlice";
import api from "../../../api/axios";

/* Every amount on /admin/orders is an integer number of PAISE. */
const rupees = (paise) =>
  ((Number(paise) || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/* ─── Constants ─────────────────────────────────────────────────
   A status is a NAME from shop.CfgOrderTransitions, never one of the old
   numeric orderStatus codes. Every state the graph can reach is listed: an
   order sitting in `assigned`, `processing_done`, `waiting_delivery`,
   `ready_pickup`, `out_for_delivery` or `completed` used to fall through to
   the "Pending" badge and to step 0 of the timeline — wrong, and silent.
   The four legacy spellings (ready / out_delivery / out_pickup / shipped)
   ride along because old rows still carry them.                          */
const STATUS_CONFIG = {
  pending:           { bg: "#faeeda", color: "#854f0b", border: "#fac775", dot: "#f59e0b", label: "Pending"             },
  accepted:          { bg: "#e6f1fb", color: "#185fa5", border: "#b5d4f4", dot: "#3b82f6", label: "Accepted"            },
  assigned:          { bg: "#e6f1fb", color: "#185fa5", border: "#b5d4f4", dot: "#3b82f6", label: "Assigned"            },
  preparing:         { bg: "#eeedfe", color: "#534ab7", border: "#afa9ec", dot: "var(--milliyo-forest)", label: "Milling / Packing" },
  processing_done:   { bg: "#eaf3de", color: "#3b6d11", border: "#c0dd97", dot: "#22c55e", label: "Milled & Packed"     },
  ready:             { bg: "#eaf3de", color: "#3b6d11", border: "#c0dd97", dot: "#22c55e", label: "Order Ready"         },
  waiting_delivery:  { bg: "#eaf3de", color: "#3b6d11", border: "#c0dd97", dot: "#22c55e", label: "Waiting for Delivery" },
  ready_pickup:      { bg: "#eaf3de", color: "#3b6d11", border: "#c0dd97", dot: "#22c55e", label: "Ready for Pickup"    },
  out_for_delivery:  { bg: "#fbeaf0", color: "#993556", border: "#f4c0d1", dot: "#ec4899", label: "Out for Delivery"    },
  out_delivery:      { bg: "#fbeaf0", color: "#993556", border: "#f4c0d1", dot: "#ec4899", label: "Out for Delivery"    },
  out_pickup:        { bg: "#fbeaf0", color: "#993556", border: "#f4c0d1", dot: "#ec4899", label: "Out for Pickup"      },
  delivered:         { bg: "#e1f5ee", color: "#0f6e56", border: "#9fe1cb", dot: "#10b981", label: "Delivered"           },
  left_at_doorstep:  { bg: "#e1f5ee", color: "#0f6e56", border: "#9fe1cb", dot: "#10b981", label: "Left at Doorstep"    },
  shipped:           { bg: "#e1f5ee", color: "#0f6e56", border: "#9fe1cb", dot: "#10b981", label: "Shipped"             },
  completed:         { bg: "#e1f5ee", color: "#0f6e56", border: "#9fe1cb", dot: "#10b981", label: "Completed"           },
  delivery_failed:   { bg: "#fef2f2", color: "#b91c1c", border: "#fca5a5", dot: "#ef4444", label: "Delivery Failed"     },
  could_not_deliver: { bg: "#fef2f2", color: "#b91c1c", border: "#fca5a5", dot: "#ef4444", label: "Could Not Deliver"   },
  cancelled:         { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db", dot: "#9ca3af", label: "Cancelled"           },
};

/* Workflow steps per fulfilment mode (order.fulfilment is "delivery" |
   "pickup"). The two modes diverge after processing_done, which is exactly
   where the graph forks: waiting_delivery/out_for_delivery for a delivery,
   ready_pickup/completed for a pickup. */
const TIMELINE_STEPS = {
  delivery: [
    { key: "pending",          label: "Ordered",           icon: "🕐" },
    { key: "accepted",         label: "Accepted",          icon: "✅" },
    { key: "preparing",        label: "Milling / Packing", icon: "👨‍🍳" },
    { key: "processing_done",  label: "Packed",            icon: "📦" },
    { key: "out_for_delivery", label: "Out for Delivery",  icon: "🚚" },
    { key: "delivered",        label: "Delivered",         icon: "🎉" },
  ],
  pickup: [
    { key: "pending",         label: "Ordered",           icon: "🕐" },
    { key: "accepted",        label: "Accepted",          icon: "✅" },
    { key: "preparing",       label: "Milling / Packing", icon: "👨‍🍳" },
    { key: "processing_done", label: "Packed",            icon: "📦" },
    { key: "ready_pickup",    label: "Ready for Pickup",  icon: "🏪" },
    { key: "completed",       label: "Collected",         icon: "🎉" },
  ],
};

/* Status → step index, per mode. A status the mode cannot reach is absent
   rather than guessed at. */
const STATUS_STEP_INDEX = {
  delivery: {
    pending:           0,
    accepted:          1,
    assigned:          1,
    preparing:         2,
    processing_done:   3,
    ready:             3,
    waiting_delivery:  3,
    out_for_delivery:  4,
    out_delivery:      4,
    shipped:           4,
    delivery_failed:   4,
    could_not_deliver: 4,
    delivered:         5,
    left_at_doorstep:  5,
    completed:         5,
    cancelled:        -1,
  },
  pickup: {
    pending:         0,
    accepted:        1,
    assigned:        1,
    preparing:       2,
    processing_done: 3,
    ready:           3,
    ready_pickup:    4,
    out_pickup:      4,
    delivered:       5,
    completed:       5,
    cancelled:      -1,
  },
};

const AVATAR_PALETTE = [
  ["#eff6ff", "var(--milliyo-forest)"], ["#faf5ff", "#7c3aed"],
  ["#f0fdf4", "#16a34a"], ["#fff7ed", "#ea580c"], ["#f0f9ff", "#0284c7"],
];

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata",  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

/* ─── Small reusable card section header ─────────────────────── */
const CardHeader = ({ iconBg, iconStroke, icon, title }) => (
  <div className="card-header" style={{ borderBottom: "1px solid var(--mly-border-soft)", paddingBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9, background: iconBg,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
          fill="none" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {icon}
        </svg>
      </div>
      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "var(--mly-text)" }}>{title}</h6>
    </div>
  </div>
);

/* ─── InfoRow ─────────────────────────────────────────────────── */
const InfoRow = ({ emoji, value, last }) => (
  <div style={{
    display: "flex", gap: 10, marginBottom: last ? 0 : 10,
    paddingBottom: last ? 0 : 10,
    borderBottom: last ? "none" : "1px solid var(--mly-border-soft)",
    alignItems: "flex-start",
  }}>
    <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{emoji}</span>
    <span style={{ fontSize: 12, color: "var(--mly-text-soft)", lineHeight: 1.6 }}>{value || "—"}</span>
  </div>
);

/* ─── Order Timeline Component ───────────────────────────────── */
const OrderTimeline = ({ order }) => {
  const deliveryMode  = order?.fulfilment || "delivery";
  const currentStatus = order?.status || "pending";
  const isCancelled   = currentStatus === "cancelled";

  const steps        = TIMELINE_STEPS[deliveryMode] || TIMELINE_STEPS.delivery;
  const indexes      = STATUS_STEP_INDEX[deliveryMode] || STATUS_STEP_INDEX.delivery;
  const currentStep  = indexes[currentStatus] ?? 0;

  return (
    <div className="card" style={{ marginBottom: 0, overflow: "hidden" }}>
      <CardHeader
        iconBg="#f0fdf4" iconStroke="#0f6e56"
        title="Order Timeline"
        icon={<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>}
      />

      <div className="card-body" style={{ padding: "24px 20px" }}>

        {/* ── Cancelled banner ── */}
        {isCancelled && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "#fef2f2", border: "1.5px solid #fca5a5",
            borderRadius: 12, padding: "12px 16px", marginBottom: 24,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#dc2626" }}>Order Cancelled</div>
              <div style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>This order has been cancelled</div>
            </div>
          </div>
        )}

        {/* ── Horizontal step bar (non-cancelled) ── */}
        {!isCancelled && (
          <>
            {/* Progress bar track */}
            <div className="sp_horizontal" style={{ position: "relative", marginBottom: 8 }}>
              <div style={{
                position: "absolute", top: 18, left: `calc(100% / ${steps.length} / 2)`,
                right: `calc(100% / ${steps.length} / 2)`,
                height: 4, background: "var(--mly-surface-3)", borderRadius: 4, zIndex: 0,
              }}>
                <div style={{
                  height: "100%", borderRadius: 4,
                  background: "linear-gradient(90deg, #0f6e56, #22c55e)",
                  width: currentStep === 0
                    ? "0%"
                    : `${(currentStep / (steps.length - 1)) * 100}%`,
                  transition: "width 0.5s ease",
                }} />
              </div>

              {/* Steps */}
              <div style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
                {steps.map((step, i) => {
                  const isDone   = i < currentStep;
                  const isActive = i === currentStep;
                  const isPast   = i > currentStep;

                  return (
                    <div key={step.key} style={{
                      display: "flex", flexDirection: "column", alignItems: "center",
                      flex: 1, gap: 8,
                    }}>
                      {/* Circle */}
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: isDone ? "#0f6e56" : isActive ? "#fff" : "var(--mly-surface-2)",
                        border: isDone
                          ? "2.5px solid #0f6e56"
                          : isActive
                            ? "2.5px solid #0f6e56"
                            : "2.5px solid var(--mly-border)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: isActive ? "0 0 0 5px #e1f5ee" : isDone ? "0 0 0 3px #e1f5ee" : "none",
                        transition: "all 0.3s",
                        position: "relative",
                      }}>
                        {isDone ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        ) : isActive ? (
                          <div style={{
                            width: 10, height: 10, borderRadius: "50%", background: "#0f6e56",
                            animation: "pulse 1.5s infinite",
                          }} />
                        ) : (
                          <span style={{ fontSize: 14, opacity: isPast ? 0.3 : 0.5 }}>{step.icon}</span>
                        )}
                      </div>

                      {/* Label */}
                      <div style={{
                        fontSize: 10, fontWeight: isActive ? 700 : isDone ? 600 : 400,
                        color: isDone ? "var(--mly-heading)" : isActive ? "var(--mly-heading)" : "var(--mly-text-muted)",
                        textAlign: "center", lineHeight: 1.3,
                        maxWidth: 70,
                      }}>
                        {step.label}
                      </div>

                      {/* Active badge */}
                      {isActive && (
                        <span style={{
                          background: "#e1f5ee", color: "#0f6e56",
                          border: "1px solid #9fe1cb",
                          fontSize: 9, fontWeight: 700,
                          padding: "2px 7px", borderRadius: 20,
                          whiteSpace: "nowrap",
                        }}>
                          Current
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Vertical detail list below ── */}
            <div className="sp_vertical" style={{
              marginTop: 28, borderTop: "1px solid var(--mly-border-soft)", paddingTop: 20,
              display: "flex", flexDirection: "column", gap: 0,
            }}>
              {steps.map((step, i) => {
                const isDone   = i < currentStep;
                const isActive = i === currentStep;
                const isLast   = i === steps.length - 1;

                return (
                  <div key={step.key} style={{ display: "flex", gap: 14, position: "relative" }}>
                    {/* Left line + dot */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 20 }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                        background: isDone ? "#0f6e56" : isActive ? "#fff" : "var(--mly-surface-3)",
                        border: `2px solid ${isDone ? "#0f6e56" : isActive ? "#0f6e56" : "var(--mly-border)"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: isActive ? "0 0 0 3px #e1f5ee" : "none",
                        zIndex: 1,
                      }}>
                        {isDone && (
                          <svg width="6" height="6" viewBox="0 0 24 24" fill="none"
                            stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                        {isActive && !isDone && (
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#0f6e56" }} />
                        )}
                      </div>
                      {!isLast && (
                        <div style={{
                          width: 2, flex: 1, minHeight: 28,
                          background: isDone ? "#0f6e56" : "var(--mly-border)",
                          borderRadius: 2, marginTop: 2,
                        }} />
                      )}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, paddingBottom: isLast ? 0 : 20 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{
                          fontWeight: isDone || isActive ? 600 : 400,
                          fontSize: 13,
                          color: isDone ? "var(--mly-text)" : isActive ? "var(--mly-heading)" : "var(--mly-text-muted)",
                        }}>
                          {step.icon} {step.label}
                        </span>
                        {isActive && (
                          <span style={{
                            background: "#e1f5ee", color: "#0f6e56",
                            border: "1px solid #9fe1cb",
                            fontSize: 10, fontWeight: 700,
                            padding: "2px 8px", borderRadius: 20,
                          }}>
                            Current
                          </span>
                        )}
                        {isDone && (
                          <span style={{
                            background: "#f0fdf4", color: "#16a34a",
                            fontSize: 10, fontWeight: 600,
                            padding: "2px 8px", borderRadius: 20,
                          }}>
                            ✓ Done
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%   { transform: scale(1);   opacity: 1; }
          50%  { transform: scale(1.4); opacity: 0.6; }
          100% { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────── */
const CustomerOrderDetails = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  /* GET /admin/orders/{orderNo} answers five siblings at the top level —
     { order, customer, items, history, siblings } — not one flat order. The
     route matches order_no OR legacy_id, so an old Mongo _id in the URL still
     resolves, and every write below addresses the order by order.orderNo. */
  const detail    = useSelector(selectOrderDetails);
  const order     = detail?.order || null;
  const customer  = detail?.customer || null;
  const items     = detail?.items || [];
  const loading   = useSelector((s) => s.orders.loading);
  const updating  = useSelector((s) => s.orders.updating);
  const error     = useSelector((s) => s.orders.error);

  const [newStatus,      setNewStatus]      = useState("");
  const [trackingUrl,    setTrackingUrl]    = useState("");
  const [trackingDirty,  setTrackingDirty]  = useState(false);
  const [savedMsg,       setSavedMsg]       = useState("");
  const [trackingError,  setTrackingError]  = useState("");

  useEffect(() => {
    dispatch(fetchOrderDetails(id));
    return () => dispatch(clearOrderDetails());
  }, [dispatch, id]);

  useEffect(() => {
    if (order) {
      /* A status is a NAME now ("pending", "out_for_delivery"), never one of
         the old numeric orderStatus codes. */
      setNewStatus(order.status || "pending");
      setTrackingUrl(order.trackingUrl || "");
      setTrackingDirty(false);
    }
  }, [order]);

  const handleSave = async () => {
    setTrackingError("");

    if (newStatus && newStatus !== order.status) {
      /* The one route that moves an order; it answers 409
         { code: "illegal_transition", message } when the graph refuses. */
      await dispatch(updateOrderStatus({ orderNo: order.orderNo, status: newStatus }));
    }

    if (trackingDirty) {
      try {
        // TODO(cutover): no server endpoint for this — the service has no
        // route that writes trackingUrl (the order carries the column, but
        // nothing exposes a write). Left calling the retired Node path rather
        // than dropping the feature silently.
        await api.patch(`/api/orders/${order.orderNo}/tracking`, { trackingUrl });
        setTrackingDirty(false);
      } catch (err) {
        setTrackingError(
          err.response?.data?.message ||
            err.response?.data?.code ||
            "Failed to update tracking URL"
        );
        return;
      }
    }

    setSavedMsg("Changes saved!");
    setTimeout(() => setSavedMsg(""), 2500);
    dispatch(fetchOrderDetails(id));
  };

  if (loading || !order) {
    return (
      <div className="container-fluid" style={{ paddingTop: 60, textAlign: "center", color: "var(--mly-text-muted)" }}>
        {loading ? (
          <>
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
            Loading order…
          </>
        ) : (
          <span style={{ color: "#ef4444" }}>{error || "Order not found."}</span>
        )}
      </div>
    );
  }

  const sc          = STATUS_CONFIG[order.status] || STATUS_CONFIG["pending"];
  /* The shipping SNAPSHOT taken when the order was placed — flat ship* columns
     on the order, never a join to the customer's current address, so an old
     bill still reads as it was issued. */
  const addr        = {
    fullName: order.shipName || customer?.name || "",
    email:    order.shipEmail || customer?.email || "",
    phone:    order.shipPhone || customer?.phone || "",
    address:  order.shipAddress || "",
    district: order.shipDistrict || "",
    landmark: order.shipLandmark || "",
  };
  const avatarSeed  = order.orderNo || "";
  const avatarIdx   = avatarSeed ? avatarSeed.charCodeAt(avatarSeed.length - 1) % AVATAR_PALETTE.length : 0;
  const avatarStyle = AVATAR_PALETTE[avatarIdx];

  return (
    <div>
      <div className="container-fluid" style={{ marginBottom: "30px" }}>

        {/* ── Top Bar ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 22, flexWrap: "wrap", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: "15px" }}>
            <button
             onClick={() => navigate(-1)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)",
                borderRadius: 8, padding: "7px 14px", cursor: "pointer",
                fontSize: 13, fontWeight: 600, color: "var(--mly-text-soft)",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--mly-surface-3)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "var(--mly-surface-2)"}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M10.6 19.4a1.2 1.2 0 0 1-1.7 0l-6.4-6.4a1.2 1.2 0 0 1 0-1.7l6.4-6.4a1.2 1.2 0 1 1 1.7 1.7L6.3 10.8H20a1.2 1.2 0 0 1 0 2.4H6.3l4.3 4.3a1.2 1.2 0 0 1 0 1.9Z" /></svg>
              Back
            </button>
            <div>
              <span style={{ fontWeight: 800, fontSize: 19, color: "var(--mly-text)", letterSpacing: "-0.3px" }}>
                {order.orderNo}
              </span>
              <span style={{ marginLeft: 10, fontSize: 12, color: "var(--mly-text-muted)" }}>
                {formatDateTime(order.placedAt)}
              </span>
            </div>
          </div>

          {/* Status + payment badges */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{
              background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`,
              borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot, display: "inline-block" }} />
              {sc.label}
            </span>
            <span style={{
              background: order.paymentStatus === "paid" ? "#e1f5ee" : "#faeeda",
              color:      order.paymentStatus === "paid" ? "#0f6e56" : "#854f0b",
              border:     `1.5px solid ${order.paymentStatus === "paid" ? "#9fe1cb" : "#fac775"}`,
              borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
            }}>
             {
  order.paymentMethod?.toUpperCase() === "RAZORPAY"
    ? "Online Payment"
    : order.paymentMethod?.toUpperCase() || "—"
}
{" · "}
{order.paymentStatus || "—"}
            </span>
            {order.trackingUrl && (
              <a
                href={order.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: "#f0f9ff", color: "#0284c7", border: "1.5px solid #bae6fd",
                  borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
                  textDecoration: "none", display: "flex", alignItems: "center", gap: 5,
                }}
              >
                Track Shipment
              </a>
            )}
          </div>
        </div>

        {/* ── Main Grid ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, alignItems: "start" }}>

          {/* ════ LEFT COLUMN ════ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Order Items */}
            <div className="card" style={{ marginBottom: 0 }}>
              <CardHeader
                iconBg="#eff6ff" iconStroke="var(--milliyo-forest)"
                title="Order Items"
                icon={<><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></>}
              />
              <div className="card-body" style={{ padding: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "var(--mly-surface-2)" }}>
                      {["Product", "Name", "Qty", "Unit Price", "Total"].map((h, i) => (
                        <th key={h} style={{
                          padding: "10px 16px",
                          textAlign: i === 0 ? "left" : i === 4 ? "right" : "center",
                          fontSize: 11, fontWeight: 700, color: "var(--mly-text-muted)",
                          textTransform: "uppercase", letterSpacing: "0.05em",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} style={{ borderTop: "1px solid var(--mly-border-soft)" }}>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                              width: 42, height: 42, borderRadius: 10,
                              background: "var(--mly-surface-3)", overflow: "hidden",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}>
                              {/* `image` is ONE full URL, not a filename to
                                  prefix with the API host. */}
                              {item.image ? (
                                <img src={item.image} alt={item.name}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                  fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                                  <polyline points="21 15 16 10 5 21"/>
                                </svg>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "var(--mly-text)" }}>
                            {item.name || "—"}
                          </div>
                          {(item.millLabel || item.variantLabel) && (
                            <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 2 }}>
                              {item.millLabel || item.variantLabel}
                            </div>
                          )}
                          {/* TODO(cutover): server does not return the per-line
                              `texts` array — no column, so the line is dropped. */}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "center", fontWeight: 700, fontSize: 14, color: "var(--mly-text)" }}>
                          {item.quantity}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "center", fontSize: 13, color: "var(--mly-text-muted)" }}>
                          ₹{rupees(item.unitPaise)}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "right", fontWeight: 700, fontSize: 14, color: "var(--mly-text)" }}>
                          ₹{rupees(item.totalPaise)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Price summary */}
                <div style={{ borderTop: "2px solid var(--mly-border-soft)", padding: "16px 20px" }}>
                  <div style={{ maxWidth: 280, marginLeft: "auto" }}>
                    {[
                      { label: "Subtotal", value: order.subtotalPaise || 0 },
                      { label: "Discount", value: order.discountPaise || 0, minus: true },
                      { label: "Shipping", value: order.shippingPaise || 0 },
                    ].map((row) => (
                      <div key={row.label} style={{
                        display: "flex", justifyContent: "space-between",
                        marginBottom: 7, fontSize: 13, color: "var(--mly-text-muted)",
                      }}>
                        <span>{row.label}</span>
                        <span style={{ color: row.minus ? "#ef4444" : undefined }}>
                          {row.minus ? "−" : ""}₹{rupees(Math.abs(row.value))}
                        </span>
                      </div>
                    ))}
                    <div style={{
                      display: "flex", justifyContent: "space-between",
                      borderTop: "1.5px solid var(--mly-border)", paddingTop: 11, marginTop: 6,
                      fontWeight: 800, fontSize: 15, color: "var(--mly-text)",
                    }}>
                      <span>Order Total</span>
                      <span style={{ color: "var(--mly-heading)" }}>₹{rupees(order.totalPaise)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Order Timeline ── */}
            <OrderTimeline order={order} />

          </div>

          {/* ════ RIGHT COLUMN ════ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Customer Card */}
            <div className="card padding" style={{ marginBottom: 0 }}>
              <CardHeader
                iconBg="#fff7ed" iconStroke="#ea580c"
                title="Customer"
                icon={<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></>}
              />
              <div className="card-body mt-3">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <div style={{
                    width: 50, height: 50, borderRadius: 13,
                    background: avatarStyle[0], color: avatarStyle[1],
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800, fontSize: 16, letterSpacing: 1,
                    border: `2px solid ${avatarStyle[1]}30`, flexShrink: 0,
                  }}>
                    {getInitials(addr.fullName)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "var(--mly-text)" }}>{addr.fullName || "—"}</div>
                    <div style={{ fontSize: 12, color: "var(--mly-text-muted)", marginTop: 1 }}>Customer</div>
                  </div>
                </div>
                <InfoRow emoji="✉️" value={addr.email} />
                <InfoRow emoji="📞" value={addr.phone} />
                <InfoRow emoji="📍" value={[addr.address, addr.landmark, addr.district].filter(Boolean).join(", ")} last />
              </div>
            </div>

            {/* Payment Card */}
            <div className="card padding" style={{ marginBottom: 0 }}>
              <CardHeader
                iconBg="#f0fdf4" iconStroke="#16a34a"
                title="Payment"
                icon={<><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></>}
              />
              <div className="card-body mt-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  {
  label: "Method",
  value:
    order.paymentMethod?.toUpperCase() === "RAZORPAY"
      ? "Online Payment"
      : order.paymentMethod?.toUpperCase() || "—",
},
                  { label: "Status",  value: order.paymentStatus || "—",
                    badge: { bg: order.paymentStatus === "paid" ? "#e1f5ee" : "#faeeda",
                             color: order.paymentStatus === "paid" ? "#0f6e56" : "#854f0b" } },
                  /* The gateway's own order reference — paymentRef is the
                     payment id, paymentOrderRef the order it was taken for. */
                  { label: "Order ID", value: order.paymentOrderRef || "—", full: true },
                ].map((item) => (
                  <div key={item.label} style={{ gridColumn: item.full ? "1 / -1" : undefined }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                      {item.label}
                    </div>
                    {item.badge ? (
                      <span style={{
                        background: item.badge.bg, color: item.badge.color,
                        fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                      }}>
                        {item.value}
                      </span>
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)", wordBreak: "break-all" }}>
                        {item.value}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Estimated Delivery Card ── */}
            {order.estimatedDeliveryAt && (
              <div className="card padding" style={{ marginBottom: 0 }}>
                <CardHeader
                  iconBg="#faf5ff" iconStroke="#7c3aed"
                  title="Estimated Delivery"
                  icon={<><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>}
                />
                <div className="card-body mt-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                      Date
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)" }}>
                      {formatDate(order.estimatedDeliveryAt)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                      Time
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)" }}>
                      {new Date(order.estimatedDeliveryAt).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata",
                        hour: "2-digit", minute: "2-digit", hour12: true,
                      })}
                    </div>
                  </div>
                  {order.estimatedMinutes && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
                        Duration
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)" }}>
                        {order.estimatedMinutes} min
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errors */}
            {(error || trackingError) && (
              <div style={{
                background: "#fef2f2", color: "#dc2626",
                padding: "8px 12px", borderRadius: 8, fontSize: 12,
              }}>{trackingError || error}</div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrderDetails;